// frontend/src/pages/admin/AdminPayouts.js
import React, { useState, useEffect } from 'react';
import { walletApi } from '../../api/wallet';
import helpers from '../../utils/helpers';
import { FaMoneyCheckAlt, FaCheck, FaTimes, FaSpinner, FaFilter, FaUniversity, FaUser } from 'react-icons/fa';

function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const data = await walletApi.getAllPayouts(filterStatus === 'all' ? null : filterStatus);
      setPayouts(data);
    } catch (err) {
      console.error("Failed to load payouts", err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (payout, status) => {
      let notes = null;
      
      if (status === 'rejected') {
          notes = prompt("Enter rejection reason (will be sent to user):");
          if (!notes) return; // Cancel if no reason provided
      } else {
          // Approval Confirmation
          const confirmMsg = `⚠️ INITIATE TRANSFER?\n\n` +
                             `Amount: ${helpers.formatCurrency(payout.amount, 'NGN')}\n` +
                             `To: ${payout.account_name}\n` +
                             `Bank: ${payout.bank_name} (${payout.account_number})\n\n` +
                             `This will immediately deduct funds from your Flutterwave Balance.`;
                             
          if (!window.confirm(confirmMsg)) return;
          notes = "Transfer Initiated via Admin Panel";
      }

      setProcessingId(payout.id);
      try {
          await walletApi.processPayout(payout.id, status, notes);
          
          alert(status === 'approved' ? "Transfer Initiated Successfully!" : "Request Rejected.");
          
          // Remove from list if filtering by pending, otherwise update status
          if (filterStatus !== 'all') {
              setPayouts(prev => prev.filter(p => p.id !== payout.id));
          } else {
              setPayouts(prev => prev.map(p => p.id === payout.id ? { ...p, status } : p));
          }
      } catch (err) {
          console.error(err);
          const errorMsg = err.data?.message || err.message || "Action failed.";
          alert(`❌ Error: ${errorMsg}`);
      } finally {
          setProcessingId(null);
      }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaMoneyCheckAlt className="text-green-600" /> Payout Requests
      </h1>

      {/* FILTERS */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex items-center gap-4 overflow-x-auto">
        <FaFilter className="text-gray-400 flex-shrink-0" />
        <div className="flex gap-2">
            {['pending', 'approved', 'rejected', 'all'].map(status => (
                <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-4 py-2 rounded-full text-sm font-bold capitalize transition-colors whitespace-nowrap ${
                        filterStatus === status 
                        ? 'bg-primary text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                    {status}
                </button>
            ))}
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading...</div>
      ) : payouts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-dashed border-gray-300">
            <p className="text-gray-500 text-lg">No {filterStatus} payouts found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {payouts.map(payout => (
            <div key={payout.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row gap-6 justify-between items-center">
              
              <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-gray-800 text-2xl text-primary">
                        {helpers.formatCurrency(payout.amount, 'NGN')}
                    </h3>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        payout.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        payout.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                    }`}>
                        {payout.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="bg-gray-50 p-3 rounded border border-gray-100">
                          <p className="text-xs text-gray-400 uppercase font-bold mb-1 flex items-center gap-1"><FaUniversity /> Bank Details</p>
                          <p className="font-bold text-gray-800">{payout.account_name}</p>
                          <p>{payout.bank_name}</p>
                          <p className="font-mono">{payout.account_number}</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded border border-gray-100">
                          <p className="text-xs text-gray-400 uppercase font-bold mb-1 flex items-center gap-1"><FaUser /> User Details</p>
                          <p>{payout.user_email}</p>
                          <p className="text-xs mt-1">Requested: {new Date(payout.created_at).toLocaleString()}</p>
                      </div>
                  </div>
                  
                  {payout.flutterwave_transfer_id && (
                      <div className="mt-2 text-xs text-green-600 font-mono">
                          Transfer ID: {payout.flutterwave_transfer_id}
                      </div>
                  )}
              </div>

              {payout.status === 'pending' && (
                  <div className="flex gap-2 w-full md:w-auto flex-col sm:flex-row">
                      <button 
                          onClick={() => handleProcess(payout, 'approved')}
                          disabled={processingId === payout.id}
                          className="bg-green-600 text-white px-6 py-3 rounded-lg text-sm font-bold hover:bg-green-700 flex items-center justify-center gap-2 shadow-md transition-transform hover:scale-105 disabled:opacity-50 disabled:scale-100"
                      >
                          {processingId === payout.id ? <FaSpinner className="animate-spin" /> : <FaCheck />} 
                          Approve & Pay
                      </button>
                      <button 
                          onClick={() => handleProcess(payout, 'rejected')}
                          disabled={processingId === payout.id}
                          className="bg-red-100 text-red-600 px-6 py-3 rounded-lg text-sm font-bold hover:bg-red-200 flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                          <FaTimes /> Reject
                      </button>
                  </div>
              )}

            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminPayouts;